import PageHero from "@/components/PageHero";
import Reveal, { Eyebrow } from "@/components/Reveal";
import { useLanguage } from "@/context/LanguageContext";
import { usePageMeta } from "@/hooks/usePageMeta";
import { IMAGES, BUSINESS } from "@/data/site";

export default function About() {
  const { t, lang } = useLanguage();
  usePageMeta(t.meta.about.title, t.meta.about.desc);
  const a = t.aboutPage;
  const chapters = [a.story, a.philosophy, a.qualityF, a.sourcing];

  return (
    <main>
      <PageHero
        eyebrow={a.eyebrow}
        title={a.heading}
        image={IMAGES.about}
        imgAlt={a.imgAlt}
      />

      <section className="py-16 md:py-24">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-6 lg:grid-cols-2 lg:gap-20 lg:px-10">
          <Reveal>
            <p className="font-serif text-3xl italic leading-snug text-gold-light sm:text-4xl">
              {a.lead1}
            </p>
            <p className="mt-8 text-base leading-relaxed text-cream/70 sm:text-lg">
              {a.lead2}
            </p>
            <p className="mt-5 text-base leading-relaxed text-cream/70 sm:text-lg">
              {a.lead3}
            </p>
          </Reveal>
          <Reveal delay={0.15} className="relative">
            <div className="absolute -inset-3 border hairline md:-inset-5" aria-hidden="true" />
            <div className="relative aspect-[4/5] overflow-hidden">
              <img
                src={IMAGES.about}
                alt={a.imgAlt}
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-[1.4s] ease-out hover:scale-[1.04]"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-y hairline bg-coal/40 py-20 md:py-24">
        <div className="mx-auto max-w-4xl px-6 text-center lg:px-10">
          <Reveal>
            <span className="font-serif text-6xl text-gold/40" aria-hidden="true">&ldquo;</span>
            <p className="-mt-6 font-serif text-2xl italic leading-relaxed text-cream sm:text-3xl">
              {a.quote}
            </p>
            <p className="mt-6 text-[11px] font-medium uppercase tracking-luxe text-gold">
              {BUSINESS.brandMessage[lang]}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <Eyebrow>{a.eyebrow}</Eyebrow>
          </Reveal>
          <div className="mt-12">
            {chapters.map((ch, i) => (
              <Reveal key={ch.n} delay={0.05}>
                <article
                  data-testid={`about-chapter-${i + 1}`}
                  className="grid gap-6 border-t hairline py-12 md:grid-cols-[120px_1fr_2fr] md:gap-12 md:py-16"
                >
                  <span className="font-serif text-5xl italic text-gold/50 md:text-6xl">
                    {ch.n}
                  </span>
                  <h2 className="font-serif text-3xl text-cream md:text-4xl">{ch.t}</h2>
                  <p className="max-w-2xl text-base leading-relaxed text-cream/70">
                    {ch.b}
                  </p>
                </article>
              </Reveal>
            ))}
            <span className="block border-t hairline" />
          </div>
        </div>
      </section>
    </main>
  );
}
